import api from './api';

/**
 * Créditos de IA da conta (1 crédito = R$ 0,01): saldo do mês (franquia do plano + comprado),
 * média de consumo por ação e recarga por pacote. O consumo é sempre do dono da conta.
 */
export interface AiCreditSummary {
  period: string;
  billing_start: string;
  billing_active: boolean;
  postpaid: boolean;
  monthly_allowance_cents: number;
  included_remaining_cents: number;
  purchased_balance_cents: number;
  available_cents: number;
  month_spent_cents: number;
  month_tokens: number;
  month_calls: number;
}

export interface AiActionAverage {
  feature: string;
  action: string | null;
  source: 'account' | 'global' | 'feature';
  uses: number;
  avg_cents: number;
  avg_tokens: number;
}

export interface AiEstimatesTable {
  averages: AiActionAverage[];
  estimates: Record<string, number>;
  default_estimate_cents: number;
}

export interface AiEstimate {
  cents: number;
  tokens: number | null;
  source: string;
}

export interface AiCreditPack {
  credits: number;
  price_brl: number;
  label: string;
  popular?: boolean;
}

/**
 * Mesma regra do backend (aiBillingService.estimativaDe): média da ação > estimativa da ação >
 * média da funcionalidade > estimativa da funcionalidade > padrão.
 */
export const estimativaDe = (tabela: AiEstimatesTable | null, feature: string, action: string | null = null): AiEstimate => {
  const lista = tabela?.averages || [];
  const estimativas = tabela?.estimates || {};
  const exata = action ? lista.find((a) => a.feature === feature && a.action === action) : undefined;
  if (exata) return { cents: exata.avg_cents, tokens: exata.avg_tokens, source: exata.source };
  if (action && estimativas[`${feature}.${action}`] != null) return { cents: Number(estimativas[`${feature}.${action}`]), tokens: null, source: 'estimate' };
  const daFuncionalidade = lista.find((a) => a.feature === feature && !a.action);
  if (daFuncionalidade) return { cents: daFuncionalidade.avg_cents, tokens: daFuncionalidade.avg_tokens, source: daFuncionalidade.source };
  if (estimativas[feature] != null) return { cents: Number(estimativas[feature]), tokens: null, source: 'estimate' };
  return { cents: Number(tabela?.default_estimate_cents ?? 20), tokens: null, source: 'default' };
};

/**
 * A ação cabe no saldo? Antes do início da cobrança e em conta pós-paga, sempre. Depois, o
 * disponível (franquia do mês + comprado) precisa cobrir a estimativa da ação.
 */
export const saldoInsuficiente = (resumo: AiCreditSummary | null, custoCentavos: number): boolean => {
  if (!resumo || !resumo.billing_active || resumo.postpaid) return false;
  return resumo.available_cents < Math.max(1, custoCentavos);
};

export const reais = (centavos: number) => (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const creditos = (centavos: number) => Math.round(centavos).toLocaleString('pt-BR');

export const aiCreditsService = {
  summary: async (): Promise<AiCreditSummary> => (await api.get('/ai-billing/summary')).data.data,
  estimates: async (): Promise<AiEstimatesTable> => (await api.get('/ai-billing/estimates')).data.data,
  packs: async (): Promise<Record<string, AiCreditPack>> => (await api.get('/ai-billing/packs')).data.data.packs,
  checkout: async (pack: string): Promise<{ url: string; session_id: string }> => (await api.post('/ai-billing/checkout', { pack })).data.data,
  confirm: async (sessionId: string): Promise<{ summary: AiCreditSummary; already_credited: boolean }> =>
    (await api.post('/ai-billing/confirm', { session_id: sessionId })).data.data
};

// Canal entre abas: a aba do pagamento avisa a aba de trabalho que a recarga entrou
export const CANAL_RECARGA = 'devskin-ai-credits';
